import useMissionCompletionPrompt from '../hooks/useMissionCompletionPrompt';
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/store-hook';
import { DrawerOpenState, drawerActions } from '../store/drawerSlice';
import useCustomTheme from '../hooks/theme-hook';
import { authActions, map as Map } from '../models/helpers/platformImportHelper';
import { GetMissionsFromMapDto, MissionsClient, MissionType } from '../api/api.g';
import useApi from '../hooks/api-hook';
import Label, { LabelType } from '../components/common/Label';
import * as Location from 'expo-location';
import { ActivityIndicator, Linking, Modal, Platform, Pressable, ScrollView, useWindowDimensions, View } from 'react-native';
import useStyles from '../hooks/styles-hook';
import MissionList from '../components/common/mission/MissionList';
import { FilterType } from '../models/MissionFilterType';
import { AirportMissionSearchContext, MissionCardProps } from '../components/common/mission/MissionCard';
import { MapLocation, MapMarker, MissionFilter } from '../models/MapModels';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import CreateResponsiveStyle from '../models/helpers/responsiveStyleHelper';
import { useRoute } from '@react-navigation/core';
import { navBarActions } from '../store/navBarSlice';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faHouseChimney, faList, faMagnifyingGlass, faMapLocationDot, faXmark } from '@fortawesome/pro-regular-svg-icons';
import useTranslation from '../hooks/translation-hook';
import { missionsActions } from '../store/missionsSlice';
import { useFocusEffect, useIsFocused, useNavigation } from '@react-navigation/native';
import { LoggedState } from '../models/AuthModelsCommon';
import { MISSION_REFERRAL_SIGN_UP_SOURCE } from '../models/SignUpSources';
import useMapLocation, { LocationPermissionStatus } from '../hooks/useMapLocation';
import AuthGuard from '../components/common/AuthGuard';
import MapFilter from '../components/common/filter/MapFilter';
import PillButton from '../components/common/PillButton';
import { lightBoxActions } from '../store/lightBoxSlice';
import useAdvertisementTrackingHook from '../hooks/advertisement-hook';
import MoreInfoButton from '../components/common/MoreInfoButton';
import { AnalyticsHelper } from '../helpers/analyticsHelper';
import { StringHelper } from '../helpers/stringHelper';
import LocationPermission from '../components/map/LocationPermission';
import useAddressMangager from '../hooks/address-manager-hook';
import MobileAppCallToAction from '../components/common/MobileAppCallToAction';
import Overlay from '../components/common/Overlay';
import Popup from '../components/common/Popup';
import MissionBranchList from '../components/common/mission/MissionBranchList';
import AccountVerificationPopup from '../components/myaccount/AccountVerificationPopup';
import OnARollPopup from '../components/common/mission/MissionOnARollPopup';
import NoNearbyMissionsPopup from '../components/common/mission/NoNearbyMissionsPopup';
import ProfileManager, { ProfileManagerHandles } from '../components/myaccount/myprofile/ProfileManager';
import { OnboardingPopupStepsEnum } from '../components/onboarding/OnboardingPopupSteps';
import AirportTripSoonPopup from '../components/onboarding/AirportTripSoonPopup';
import AgeRestrictedMissionsPopup from '../components/onboarding/AgeRestrictedMissionsPopup';
import { isMissionAvailableForDiscovery } from '../models/helpers/missionAvailabilityHelper';
import { hasFamilyCampaignContext, readCampaignLanding } from '../helpers/campaignEntry';
import useOnboardingFlows from '../hooks/useOnboardingFlows';
import usePopupSession from '../hooks/usePopupSession';
import useMapViewport, { MapMode } from '../hooks/useMapViewport';
import useMissionsData from '../hooks/useMissionsData';
import useMarkers from '../hooks/useMarkers';
import useMapSidebarState, { SideBarState } from '../hooks/useMapSidebarState';
import { myAccountActions } from '../store/myAccountSlice';
import axios from 'axios';
import { getInitialDiscoveryBounds } from '../components/map/initialDiscoveryViewport';
import CompactMissionFilters from '../components/common/mission/CompactMissionFilters';
import PinnedMissionsButton from '../components/common/mission/PinnedMissionsButton';
import { competitorCircuitCompetitorIcon } from '../components/map/competitorCircuitMarkerIcons';
import { getCompetitorCircuitBounds } from '../components/map/competitorCircuitViewport';
import { resolveMapSelectedId } from '../components/map/nativeMarkerSelection';
import { hasMapRouteSelection, shouldClearMapSelectionForReset, getRetainedMapMissionDetail } from '../models/helpers/mapRouteSelectionHelper';

const INITIAL_ZOOM_WIDE_AREA = 9;
const MAX_ZOOM_IN = 16;
const MAX_ZOOM_OUT = 6;
const LIST_SELECTION_ZOOM = 15;
const PROGRAMMATIC_INTERACTION_SUPPRESSION_MS = 1700;
const AGE_RESTRICTED_PROMOTION_RADIUS_KM = 15;
const NO_NEARBY_MISSIONS_RADIUS_KM = 800;
const FLASH_MISSIONS_ONBOARDING_CODE = 'FlashMissionsOnboarding';
const AIRPORT_TRIP_SOON_POPUP_CODE = 'AirportTripSoonPopup';
const AGE_RESTRICTED_MISSIONS_POPUP_CODE = 'AgeRestrictedMissionsPopup';
const NO_NEARBY_MISSIONS_POPUP_CODE = 'NoNearbyMissionsPopup';
const AIRPORT_TRIP_SOON_POPUP_DELAY_MS = 20_000;
const AGE_RESTRICTED_MISSIONS_POPUP_DELAY_MS = 20_000;
let airportTripSoonPopupShownInSession = false;
let ageRestrictedMissionsPopupShownInSession = false;
let noNearbyMissionsPopupShownInSession = false;

const getMapLocationKey = (location?: MapLocation) => {
  if (!location || !Number.isFinite(location.lat) || !Number.isFinite(location.lng)) {
    return undefined;
  }

  return `${Number(location.lat).toFixed(5)}:${Number(location.lng).toFixed(5)}`;
};

const getBoundingBoxAroundCenter = (centerLat: number, centerLng: number, distanceKm: number) => {
  const latDiff = distanceKm / 110.574;
  const lonDegreesPerKm = Math.max(0.0001, Math.abs(111.32 * Math.cos((centerLat * Math.PI) / 180)));
  const lonDiff = distanceKm / lonDegreesPerKm;

  return {
    minLat: centerLat - latDiff,
    maxLat: centerLat + latDiff,
    minLng: centerLng - lonDiff,
    maxLng: centerLng + lonDiff,
  };
};

const getDistanceKm = (from: MapLocation, to: MapLocation) => {
  const earthRadiusKm = 6371;
  const toRadians = (value: number) => (value * Math.PI) / 180;
  const latDelta = toRadians(to.lat - from.lat);
  const lngDelta = toRadians(to.lng - from.lng);
  const fromLat = toRadians(from.lat);
  const toLat = toRadians(to.lat);
  const haversine =
    Math.sin(latDelta / 2) * Math.sin(latDelta / 2) +
    Math.cos(fromLat) * Math.cos(toLat) * Math.sin(lngDelta / 2) * Math.sin(lngDelta / 2);

  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
};

const CustomMap = () => {
  const apiMissions = useApi(MissionsClient);
  const apiAirportMissions = useApi(MissionsClient);
  const layout = useWindowDimensions();
  const route = useRoute();
  const t = useTranslation();
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const isMapFocused = useIsFocused();
  const responsiveStyles = responsiveStyle(layout);
  const styles = useStyles((section) => section.customMap);
  const theme = useCustomTheme();
  const dispatch = useAppDispatch();
  const authStore = useAppSelector((selector) => selector.auth);
  const { isActive: isMapPopupActive, isCurrent: isCurrentMapPopupSession } = usePopupSession(isMapFocused);
  const drawerState = useAppSelector((selector) => selector.drawer.state);
  const drawerSuspendCount = useAppSelector((selector) => selector.drawer.suspendCount);
  const isLightBoxVisible = useAppSelector((selector) => selector.lightBox.visible);
  const isLightBoxBackgroundInteractive = useAppSelector((selector) => selector.lightBox.allowBackgroundInteraction ?? false);
  const isSecondLightBoxVisible = useAppSelector((selector) => selector.lightBox.secondVisible);
  const isModalVisible = useAppSelector((selector) => selector.modal.visible);
  const isMyAccountVisible = useAppSelector((selector) => selector.myAccount.visible);
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  const missionsStore = useAppSelector((selector) => selector.missions);
  const advertisementTracking = useAdvertisementTrackingHook();
  const addressManager = useAddressMangager();
  const profileManager = useRef<ProfileManagerHandles>(null);

  const {
    fallBackLocation,
    serviceLocation,
    effectiveLocation,
    hasResolvedUserPosition,
    currentLocation,
    userLocation,
    askLocation,
    locationPermissionGranted,
    locationPermissionStatus,
    locationPermissionCanAskAgain,
    waitingForBrowserLocation,
    gpsFallbackToast,
    dismissLocation,
    setDismissLocation,
    showedLocationPopup,
    setShowedLocationPopup,
    hasGpsFix,
    locationReady,
    triggerGetUserLocation,
    setTriggerGetUserLocation,
    getUserCurrentLocation: internalGetUserCurrentLocation,
    setLocationPermissionState,
    useDefaultLocation,
  } = useMapLocation();
  const [missionFilter, setMissionFilter] = useState<MissionFilter>();
  const [cleanSelection, setCleanSelection] = useState(false);
  const isWideLayout = layout.width > 1200;
  const useDrawerFilterOnMobile = Platform.OS !== 'web' && !isWideLayout;
  const showMobileListView = !isWideLayout && missionsStore.showMissionListView;
  const showFilters = isWideLayout || !missionsStore.showMissionListView;
  const mobileToggleIconColor = (theme.isDark ? theme.text?.primary ?? 'white' : theme.background?.darkTurquoise ?? theme.text?.primary ?? 'black').toString();
  const [missionQuerySeed, setMissionQuerySeed] = useState<string | undefined>(undefined);
  const [openFilter, setOpenFilter] = useState(Platform.OS === 'web' && isWideLayout);
  const [compactFiltersExpanded, setCompactFiltersExpanded] = useState(true);
  const listMapBoundsRef = useRef<typeof activeBounds>(undefined);
  const [refreshMap, setRefreshMap] = useState(false);
  const [awaitingAndroidInitialViewportResults, setAwaitingAndroidInitialViewportResults] = useState(false);
  const [isAirportBookingVisibleOnMobile, setIsAirportBookingVisibleOnMobile] = useState(false);
  const [airportBannerPopupRequest, setAirportBannerPopupRequest] = useState<{
    branchId: string;
    key: string;
    openStep?: 'routeForm';
    source?: 'takeMorePopup' | 'banner';
  }>();
  const [airportStartupBookingRequest, setAirportStartupBookingRequest] = useState<{
    branchId: string;
    key: string;
    returnToListView: boolean;
  }>();
  const [resetAirportFlowKey, setResetAirportFlowKey] = useState<string | undefined>(undefined);
  const [airportAcceptStartDate, setAirportAcceptStartDate] = useState<Date | undefined>(undefined);
  const [selectedMissionCardForDetails, setSelectedMissionCardForDetails] = useState<MissionCardProps | undefined>(undefined);
  const [airportTripSoonTimerElapsed, setAirportTripSoonTimerElapsed] = useState(false);
  const [airportTripSoonPopupHandled, setAirportTripSoonPopupHandled] = useState(airportTripSoonPopupShownInSession);
  const [ageRestrictedMissionsTimerElapsed, setAgeRestrictedMissionsTimerElapsed] = useState(false);
  const [ageRestrictedMissionsPopupHandled, setAgeRestrictedMissionsPopupHandled] = useState(ageRestrictedMissionsPopupShownInSession);
  const [showNoNearbyMissionsPopup, setShowNoNearbyMissionsPopup] = useState(false);
  const [noNearbyMissionsPopupHandled, setNoNearbyMissionsPopupHandled] = useState(noNearbyMissionsPopupShownInSession);
  const hasFocusedMapRef = useRef(false);
  const lastSmartZoomQueryTsRef = useRef<number>(0);
  const suppressUserInteractionUntilRef = useRef<number>(0);
  const hasAutoNativePermissionAttemptRef = useRef(false);
  const serviceFallbackRecenterKeyRef = useRef<string | undefined>(undefined);
  const preserveAirportFlowOnNextCleanSelectionRef = useRef(false);

  const clearMapSelectionRouteParams = () => {
    if (Platform.OS === 'web') {
      window.history.pushState({}, '', `/map`);
      return;
    }

    const currentPos = route?.params?.['pos'];
    const currentLocationParam = route?.params?.['location'];
    const currentAirportPopupKey = route?.params?.['airportPopupKey'];
    const currentAirportOpenStep = route?.params?.['airportOpenStep'];
    const currentAirportOpenSource = route?.params?.['airportOpenSource'];
    if (
      currentPos !== undefined ||
      currentLocationParam !== undefined ||
      currentAirportPopupKey !== undefined ||
      currentAirportOpenStep !== undefined ||
      currentAirportOpenSource !== undefined
    ) {
      navigation.setParams({
        pos: undefined,
        location: undefined,
        airportPopupKey: undefined,
        airportOpenStep: undefined,
        airportOpenSource: undefined,
      } as any);
    }
  };

  const {
    sideBarState,
    setSideBarState,
    branchMarker,
    setBranchMarker,
    preSelectedBranch,
    setPreSelectedBranch,
    moreInfoTitle,
    moreInfoContent,
    missionListFilter,
    setMissionListFilter,
    openListRequestId,
    setOpenListRequestId,
    handleOnBackPress,
    handleExploreOtherMissionsFromAirportLimit,
    handleMoreInfoBack,
    handleReadMorePress,
    handleAdmissibilityPress,
    handleConditionsPress,
    handleMarkerClickCallBack,
  } = useMapSidebarState({
    layout,
    theme,
    t,
    drawerState,
    drawerSuspendCount,
    isModalVisible,
    isLightBoxVisible,
    isLightBoxBackgroundInteractive,
    isSecondLightBoxVisible,
    setResetAirportFlowKey,
    setTriggerGetUserLocation,
    setRefreshMap,
    setCleanSelection,
    clearMapSelectionRouteParams,
    dispatch,
  });

  const handleAcceptedMission = useCallback(
    (missionAssignationId?: string) => {
      handleOnBackPress(false);

      setTimeout(() => {
        navigation.navigate({ name: 'mymissions', params: missionAssignationId ? { id: missionAssignationId } : undefined } as never);
      }, 0);
    },
    [handleOnBackPress, navigation]
  );

  const {
    mapZoom,
    discoveryViewport,
    handleViewportChange,
    setMapZoom,
    mapMode,
    setMapMode,
    autoZoomOnFirstLoad,
    setAutoZoomOnFirstLoad,
    hasCenteredOnEffectiveLocation,
    lastMissionsViewCenter,
    lastMissionsViewZoom,
    setLastMissionsViewCenter,
    setLastMissionsViewZoom,
    viewportCommand,
    setViewportCommand,
    activeBounds,
    setActiveBounds,
    topLeftLocation,
    bottomRightLocation,
    setTopLeftLocation,
    setBottomRightLocation,
    appliedTopLeftLocation,
    appliedBottomRightLocation,
    setAppliedTopLeftLocation,
    setAppliedBottomRightLocation,
    showNoMissionOverlay,
    setShowNoMissionOverlay,
    suppressSearchAreaDuringInitialZoom,
    setSuppressSearchAreaDuringInitialZoom,
    hasViewportBounds,
    hasCenteredOnEffectiveLocationRef,
    userHasPannedSinceProgrammaticMoveRef,
    applyNextProgrammaticBoundsRef,
    ignoreNextBoundsFromClusterRef,
    restoreViewAfterMapReadyRef,
    ignoreNextBoundsFromRestoreRef,
    handleBoundsChange,
    cancelPendingViewportSearch,
    resetMapToInitialLoad,
  } = useMapViewport({
    effectiveLocation,
    fallBackLocation,
    userLocation,
    waitingForBrowserLocation,
    hasGpsFix,
    preSelectedBranchId: preSelectedBranch?.id,
  });

  const {
    missions,
    airportMapGroups,
    missionCardProps,
    setMissionCardProps,
    airportMissionCardProps,
    airportMapGroupsState: _airportMapGroupsState,
    setAirportMapGroups,
    pinnedMissionCardProps,
    setPinnedMissionCardProps,
    flashMissionCardProps,
    setFlashMissionCardProps,
    initialMissionCardProps,
    initialPinnedMissionCardProps,
    initialFlashMissionCardProps,
    filterItems,
    setFilterItems,
    selectedFilterItems,
    setSelectedFilterItems,
    onlyAcceptableMissions,
    setOnlyAcceptableMissions,
    showUpcomingMissions,
    setShowUpcomingMissions,
    canFetchMapMissions,
    canFetchAirportMissions,
    loadingSideContent,
    shouldShowMapMissionLoading,
    hasLoadedMissions,
    hasLoadedViewportMapResults,
    mapQuery,
    airportMapQuery,
    selectedBranchMissionDetailQuery,
    selectedMissionFallback,
  } = useMissionsData({
    discoveryViewport,
    apiMissions,
    apiAirportMissions,
    authToken: authStore.idToken,
    uiLanguage: userPrefs.uiLanguage,
    effectiveLocation,
    serviceLocation,
    fallBackLocation,
    userLocation,
    currentLocation,
    locationReady,
    hasResolvedUserPosition,
    waitingForBrowserLocation,
    hasGpsFix,
    mapMode,
    hasViewportBounds,
    appliedTopLeftLocation,
    appliedBottomRightLocation,
    missionQuerySeed,
    hasCenteredOnEffectiveLocation,
    sideBarState,
    branchMarker,
    selectedMissionCardForDetailsId: selectedMissionCardForDetails?.id,
  });

  const noNearbyMissionSearchLocation = useMemo<MapLocation | undefined>(() => {
    if (hasGpsFix && userLocation && Number.isFinite(userLocation.lat) && Number.isFinite(userLocation.lng) && !userLocation.isDefault) {
      return userLocation;
    }

    if (serviceLocation && Number.isFinite(serviceLocation.lat) && Number.isFinite(serviceLocation.lng)) {
      return serviceLocation;
    }

    return undefined;
  }, [hasGpsFix, serviceLocation, userLocation]);
  const noNearbyMissionSearchLocationKey = getMapLocationKey(noNearbyMissionSearchLocation);
  const shouldCheckNoNearbyMissions =
    authStore.logged !== LoggedState.NotLogged &&
    locationReady &&
    !!noNearbyMissionSearchLocation &&
    !!noNearbyMissionSearchLocationKey &&
    advertisementTracking.loaded &&
    advertisementTracking.showAdvertisement(NO_NEARBY_MISSIONS_POPUP_CODE) &&
    !apiMissions.isCancelled;
  const noNearbyMissionsQuery = useQuery({
    enabled: shouldCheckNoNearbyMissions,
    retry: false,
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    queryKey: ['customMap', 'noNearbyMissions', noNearbyMissionSearchLocationKey, authStore.logged],
    queryFn: ({ signal }) => {
      const requestToken = axios.CancelToken.source();
      signal?.addEventListener('abort', () => {
        requestToken.cancel('getNoNearbyMissions: Query was cancelled by React Query');
      });

      const bounds = getBoundingBoxAroundCenter(
        noNearbyMissionSearchLocation!.lat,
        noNearbyMissionSearchLocation!.lng,
        NO_NEARBY_MISSIONS_RADIUS_KM,
      );

      return apiMissions.getMissionsFromMapv2(
        [],
        `${noNearbyMissionSearchLocation!.lat}&${noNearbyMissionSearchLocation!.lng}`,
        `${bounds.minLat}&${bounds.maxLng}`,
        `${bounds.maxLat}&${bounds.minLng}`,
        false,
        true,
        true,
        undefined,
        requestToken.token,
      );
    },
  });
  const noNearbyMissionCount = useMemo(() => {
    const result = noNearbyMissionsQuery.data;
    if (!result || !noNearbyMissionSearchLocation) {
      return 0;
    }

    return [...(result.highOrderMissions ?? []), ...(result.missions ?? []), ...(result.lowOrderMissions ?? [])].filter((mission) => {
      const lat = Number(mission.geocoordinate?.y);
      const lng = Number(mission.geocoordinate?.x);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return false;
      }

      return getDistanceKm(noNearbyMissionSearchLocation, { lat, lng }) <= NO_NEARBY_MISSIONS_RADIUS_KM;
    }).length;
  }, [noNearbyMissionSearchLocation, noNearbyMissionsQuery.data]);
  const suppressProgrammaticInteraction = useCallback((durationMs: number = PROGRAMMATIC_INTERACTION_SUPPRESSION_MS) => {
    suppressUserInteractionUntilRef.current = Date.now() + durationMs;
  }, []);
  const handleMissionCardSelect = useCallback(
    (missionCard?: MissionCardProps) => {
      const id = missionCard?.id;
      const lat = Number(missionCard?.geocoordinate?.y);
      const lng = Number(missionCard?.geocoordinate?.x);

      if (!id || !Number.isFinite(lat) || !Number.isFinite(lng)) {
        return;
      }

      setSelectedMissionCardForDetails(missionCard);
      const mainCenter: MapLocation = { lat, lng };
      const circuitBounds = Platform.OS === 'ios' && missionCard.isCompetitorCircuit
        ? getCompetitorCircuitBounds(mainCenter, missionCard.competitorLocations)
        : undefined;
      const center = circuitBounds?.center ?? mainCenter;
      userHasPannedSinceProgrammaticMoveRef.current = false;
      setLastMissionsViewCenter(center);
      setLastMissionsViewZoom(LIST_SELECTION_ZOOM);
      setMapZoom(LIST_SELECTION_ZOOM);
      setMapMode(MapMode.USER_VIEWPORT_MODE);
      suppressProgrammaticInteraction(1400);
      setViewportCommand(circuitBounds
        ? {
            id: Date.now(),
            type: 'fitBounds',
            northEast: circuitBounds.northEast,
            southWest: circuitBounds.southWest,
            padding: 64,
            suppressBoundsCallback: true,
          }
        : {
            id: Date.now(),
            type: 'centerZoom',
            center,
            zoom: LIST_SELECTION_ZOOM,
            suppressBoundsCallback: true,
          });

      handleMarkerClickCallBack(id, lat, lng);
    },
    [
      userHasPannedSinceProgrammaticMoveRef,
      setLastMissionsViewCenter,
      setLastMissionsViewZoom,
      setMapZoom,
      setMapMode,
      setViewportCommand,
      suppressProgrammaticInteraction,
      handleMarkerClickCallBack,
    ],
  );
  const handleMapMarkerSelect = useCallback(
    (id: string, lat: number, lng: number) => {
      setSelectedMissionCardForDetails(undefined);
      const selectedMission = missions?.find((mission) => mission.branchId === id);
      const circuitBounds = Platform.OS === 'ios' && selectedMission?.isCompetitorCircuit
        ? getCompetitorCircuitBounds({ lat, lng }, selectedMission.competitorLocations)
        : undefined;

      if (circuitBounds) {
        userHasPannedSinceProgrammaticMoveRef.current = false;
        setLastMissionsViewCenter(circuitBounds.center);
        suppressProgrammaticInteraction(1400);
        setViewportCommand({
          id: Date.now(),
          type: 'fitBounds',
          northEast: circuitBounds.northEast,
          southWest: circuitBounds.southWest,
          padding: 64,
          suppressBoundsCallback: true,
        });
      }

      return handleMarkerClickCallBack(id, lat, lng, 'map-marker');
    },
    [
      handleMarkerClickCallBack,
      missions,
      setLastMissionsViewCenter,
      setViewportCommand,
      suppressProgrammaticInteraction,
      userHasPannedSinceProgrammaticMoveRef,
    ],
  );
  const interactiveMissionCards = useMemo(() => {
    const attachMapSelectionPress = (cards: MissionCardProps[]) =>
      (cards ?? []).map((card) => ({
        ...card,
        onPress: (selectedCard?: MissionCardProps) => {
          handleMissionCardSelect(selectedCard ?? card);
        },
      }));

    return {
      missionCards: attachMapSelectionPress(missionCardProps),
      pinnedMissionCards: attachMapSelectionPress(pinnedMissionCardProps),
      flashMissionCards: attachMapSelectionPress(flashMissionCardProps),
      airportMissionCards: attachMapSelectionPress(airportMissionCardProps),
    };
  }, [missionCardProps, pinnedMissionCardProps, flashMissionCardProps, airportMissionCardProps, handleMissionCardSelect]);

  type DetailPanelState = 'loading' | 'ready' | 'unavailable' | 'error';

  const detailLocation = currentLocation ?? userLocation ?? serviceLocation ?? fallBackLocation;
  const selectedMissionCardFallback = useMemo(() => {
    if (!branchMarker || selectedMissionCardForDetails?.id !== branchMarker) {
      return undefined;
    }

    const missionFallback = new GetMissionsFromMapDto();
    missionFallback.branchId = branchMarker;
    missionFallback.listingKey = selectedMissionCardForDetails.listingKey;
    missionFallback.periodMissionConfigurationIds = selectedMissionCardForDetails.periodMissionConfigurationIds;
    missionFallback.promotionDisplay = selectedMissionCardForDetails.promotionDisplay;
    missionFallback.name = selectedMissionCardForDetails.title;
    missionFallback.hasLogo = !!selectedMissionCardForDetails.logoUrl;
    missionFallback.logoUrl = selectedMissionCardForDetails.logoUrl;
    missionFallback.address = selectedMissionCardForDetails.address;
    missionFallback.tags = selectedMissionCardForDetails.industry;
    missionFallback.canAccept = selectedMissionCardForDetails.canAccept;
    missionFallback.isAvailableForDiscovery = selectedMissionCardForDetails.isAvailableForDiscovery;
    missionFallback.discoveryAvailabilityStatus = selectedMissionCardForDetails.discoveryAvailabilityStatus;
    missionFallback.geocoordinate = selectedMissionCardForDetails.geocoordinate;
    missionFallback.missionType = selectedMissionCardForDetails.missionType;
    missionFallback.branchAirportInfo = selectedMissionCardForDetails.branchAirportInfo;
    missionFallback.isFlashMission = selectedMissionCardForDetails.isFlashMission;
    missionFallback.hasAgeRestrictedMissions = selectedMissionCardForDetails.hasAgeRestrictedMissions;
    missionFallback.upcomingPeriod = selectedMissionCardForDetails.upcomingPeriod;
    missionFallback.upcomingPeriodStartDate = selectedMissionCardForDetails.upcomingPeriodStartDate;
    missionFallback.missionConfigurationNames = selectedMissionCardForDetails.missionDetailCardProps
      .map((detail) => detail.title)
      .filter((title): title is string => !!title);

    const distanceText = selectedMissionCardForDetails.distance?.trim().toLowerCase();
    if (distanceText?.endsWith('km')) {
      const kmValue = Number(distanceText.slice(0, -2));
      if (Number.isFinite(kmValue)) {
        missionFallback.distance = kmValue * 1000;
      }
    }

    return missionFallback;
  }, [branchMarker, selectedMissionCardForDetails]);
  const activeMissionReferralMapTarget =
    missionsStore.missionReferralMapTarget?.branchId === branchMarker ? missionsStore.missionReferralMapTarget : undefined;
  const lastDetailMissionRef = useRef<{ branchId: string; accountId?: string; mission: GetMissionsFromMapDto } | undefined>(undefined);
  const detailMissionFromMap = useMemo(() => {
    const explicitlyReferred = activeMissionReferralMapTarget && missions?.find(mission => mission.branchId === branchMarker &&
      mission.periodMissionConfigurationIds?.includes(activeMissionReferralMapTarget.periodMissionConfigurationId));
    const base = explicitlyReferred || selectedMissionCardFallback
      || missions?.find(mission => mission.branchId === branchMarker && !mission.upcomingPeriod)
      || missions?.find(mission => mission.branchId === branchMarker) || selectedMissionFallback
      || getRetainedMapMissionDetail(lastDetailMissionRef.current, branchMarker, authStore.user?.uid);
    if (!base || selectedMissionCardFallback && !explicitlyReferred) return base;
    // A POS pin aggregates listings. Include its referred configurations even
    // when the representative pin's ordinary listing contains different IDs.
    const referredIds = (missions ?? []).filter(mission => mission.branchId === branchMarker &&
      (mission as GetMissionsFromMapDto & { isReferral?: boolean }).isReferral)
      .flatMap(mission => mission.periodMissionConfigurationIds ?? []);
    if (!referredIds.length) return base;
    return Object.assign(new GetMissionsFromMapDto(), base, {
      periodMissionConfigurationIds: [...new Set([...(base.periodMissionConfigurationIds ?? []), ...referredIds])],
    });
  }, [activeMissionReferralMapTarget, branchMarker, missions, selectedMissionCardFallback, selectedMissionFallback, authStore.user?.uid]);
  useEffect(() => {
    // Retain the open detail while inventory refreshes or acceptance consumes a
    // referral. Closing selection/account switching releases the snapshot.
    lastDetailMissionRef.current = branchMarker && detailMissionFromMap
      ? { branchId: branchMarker, accountId: authStore.user?.uid, mission: detailMissionFromMap } : undefined;
  }, [branchMarker, detailMissionFromMap, authStore.user?.uid]);
  const selectedAirportMissionSearchContext = useMemo<AirportMissionSearchContext | undefined>(() => {
    if (!branchMarker || selectedMissionCardForDetails?.id !== branchMarker) {
      return undefined;
    }

    return selectedMissionCardForDetails.airportSearchContext;
  }, [branchMarker, selectedMissionCardForDetails]);
  const detailSpinnerColor = (theme.text?.primary ?? 'black').toString();

  useEffect(() => {
    if (!branchMarker) {
      setSelectedMissionCardForDetails(undefined);
    }
  }, [branchMarker]);

  const detailPanelState = useMemo<DetailPanelState>(() => {
    if (sideBarState !== SideBarState.Details || !branchMarker) {
      return 'loading';
    }

    if (detailMissionFromMap && detailLocation) {
      return 'ready';
    }

    if (selectedBranchMissionDetailQuery.isError) {
      return 'error';
    }

    if (selectedBranchMissionDetailQuery.isSuccess) {
      return 'unavailable';
    }

    if (selectedBranchMissionDetailQuery.isFetching) {
      return 'loading';
    }

    return 'unavailable';
  }, [
    sideBarState,
    branchMarker,
    detailMissionFromMap,
    detailLocation,
    selectedBranchMissionDetailQuery.isError,
    selectedBranchMissionDetailQuery.isSuccess,
    selectedBranchMissionDetailQuery.isFetching,
  ]);

  const detailPanelContent = useMemo<ReactElement>(() => {
    if (detailPanelState === 'ready' && detailMissionFromMap && detailLocation) {
      const isAirportMissionDetail = detailMissionFromMap.missionType === MissionType.Airport;
      return (
        <MissionBranchList
          missionFromMap={detailMissionFromMap}
          location={detailLocation}
          airportStartDate={airportAcceptStartDate}
          airportSearchContext={selectedAirportMissionSearchContext}
          // Mobile renders this panel through Redux-backed drawer state.
          // Avoid freezing the shared ref object inside stored React elements,
          // or React will later fail when clearing `current` during navigation.
          profileManagerRef={isWideLayout ? profileManager : undefined}
          onBackPress={() => {
            preserveAirportFlowOnNextCleanSelectionRef.current = isAirportMissionDetail;
            handleOnBackPress(false);
          }}
          onAcceptedMission={handleAcceptedMission}
          onExploreOtherMissionsPress={handleExploreOtherMissionsFromAirportLimit}
          onMoreInfoPress={handleReadMorePress}
          onAdmissibilityPress={handleAdmissibilityPress}
          onConditionsPress={handleConditionsPress}
          missionReferralToken={activeMissionReferralMapTarget?.token}
          referralPeriodMissionConfigurationId={activeMissionReferralMapTarget?.periodMissionConfigurationId}
          onMissionReferralConsumed={() => dispatch(missionsActions.setMissionReferralMapTarget(undefined))}
        />
      );
    }

    if (detailPanelState === 'loading') {
      return (
        <View style={{ height: 150, padding: 16, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator size="small" color={detailSpinnerColor} />
        </View>
      );
    }

    return (
      <View style={{ flexGrow: 1, minHeight: 220, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 24 }}>
        <Label variant={LabelType.titleDark} style={{ textAlign: 'center' }}>
          {detailPanelState === 'unavailable' ? t('MissionUnavailable') : t('missions-load-error')}
        </Label>
        {detailPanelState === 'error' && (
          <Pressable accessibilityRole="button" onPress={() => selectedBranchMissionDetailQuery.refetch()} style={{ marginTop: 16 }}>
            <Label>{t('retry')}</Label>
          </Pressable>
        )}
        <Pressable onPress={() => handleOnBackPress(false)} style={{ marginTop: 16 }}>
          <Label variant={LabelType.caption} style={{ textTransform: 'lowercase' }}>
            {t('back')}
          </Label>
        </Pressable>
      </View>
    );
  }, [
    detailPanelState,
    t,
    detailMissionFromMap,
    selectedMissionCardFallback,
    detailLocation,
    airportAcceptStartDate,
    selectedAirportMissionSearchContext,
    activeMissionReferralMapTarget,
    handleOnBackPress,
    handleAcceptedMission,
    handleExploreOtherMissionsFromAirportLimit,
    handleReadMorePress,
    handleAdmissibilityPress,
    handleConditionsPress,
    detailSpinnerColor,
    isWideLayout,
  ]);

  useEffect(() => {
    if (resetAirportFlowKey) {
      setAirportAcceptStartDate(undefined);
    }
  }, [resetAirportFlowKey]);

  const moreInfoPanelContent = useMemo<ReactElement>(() => {
    return (
      <View key={moreInfoTitle} style={{ flexGrow: 1 }}>
        {moreInfoContent}
      </View>
    );
  }, [moreInfoTitle, moreInfoContent]);

  const desktopSideBarContent = useMemo<ReactElement | undefined>(() => {
    if (sideBarState === SideBarState.Details) {
      return detailPanelContent;
    }

    if (sideBarState === SideBarState.MoreInfo) {
      return moreInfoPanelContent;
    }

    return undefined;
  }, [sideBarState, detailPanelContent, moreInfoPanelContent]);
  const mapSelectedId = useMemo(() => {
    return resolveMapSelectedId(
      Platform.OS,
      sideBarState === SideBarState.List,
      branchMarker,
      missionsStore.branchId,
    );
  }, [branchMarker, missionsStore.branchId, sideBarState]);

  useEffect(() => {
    if (layout.width > 1200) {
      return;
    }

    if (sideBarState === SideBarState.Details) {
      dispatch(drawerActions.setContent(detailPanelContent));
      return;
    }

    if (sideBarState === SideBarState.MoreInfo) {
      dispatch(drawerActions.setContent(moreInfoPanelContent));
    }
  }, [layout.width, sideBarState, detailPanelContent, moreInfoPanelContent, dispatch]);
  const { markers, updateMarkers, clearMarkers } = useMarkers({
    missions,
    airportMapGroups,
    filterItems,
    selectedFilterItems,
    selectedMissionFilterType: missionListFilter,
    authLoggedState: authStore.logged,
    t,
  });

  const mapFilterItemsForList = useMemo(() => {
    return (filterItems ?? []).map((f) => ({
      ...f,
      checked: (selectedFilterItems ?? []).some((s) => `${s.id}` === `${f.id}`),
    }));
  }, [filterItems, selectedFilterItems]);
  // Ensure `cleanSelection` is a one-shot flag for the Map component.
  useEffect(() => {
    if (cleanSelection) {
      const raf = requestAnimationFrame(() => setCleanSelection(false));
      return () => cancelAnimationFrame(raf);
    }
  }, [cleanSelection]);

  const missionReferralPopupPending = authStore.signUpSource === MISSION_REFERRAL_SIGN_UP_SOURCE && authStore.logged === LoggedState.Logged;

  const campaignLanding = readCampaignLanding();
  const isCampaignEntry = !!campaignLanding && campaignLanding.uid === authStore.user?.uid;
  const hasCampaignDestination = Platform.OS === 'web' && hasFamilyCampaignContext((route.params as { campaign?: unknown } | undefined)?.campaign, window.location.search);
  useEffect(() => {
    if (!hasCampaignDestination || !authStore.user?.uid) {
      return;
    }
    setMissionListFilter(FilterType.AgeRestricted);
    setSideBarState(SideBarState.List);
    setBranchMarker(undefined);
    dispatch(missionsActions.setShowMissionListView(true));
    navigation.setParams({ campaign: undefined } as never);
  }, [authStore.user?.uid, hasCampaignDestination, navigation]);

  const { onboardingPopupSteps, setOnboardingPopupSteps } = useOnboardingFlows({
    enabled: isMapFocused,
    authLoggedState: authStore.logged,
    userPrefs,
    addressManager,
    profileManagerRef: profileManager,
    advertisementTracking,
    currentLocation,
    missionReferralPopupPending,
    deferCampaignOnboarding: isCampaignEntry,
  });

  const { visible: showOnARollPopup, dismiss: dismissOnARollPopup } = useMissionCompletionPrompt(
    isMapFocused && !missionReferralPopupPending && onboardingPopupSteps === OnboardingPopupStepsEnum.Completed,
  );

  const isIdleMapView = sideBarState === SideBarState.List && drawerState === DrawerOpenState.closed && !branchMarker;
  const visiblePinnedMissionCount = pinnedMissionCardProps.length;
  const hasBlockingMapOverlay = showOnARollPopup || isLightBoxVisible || isSecondLightBoxVisible || isModalVisible || isMyAccountVisible;
  const isIdlePromotionTimerReady =
    isMapPopupActive &&
    advertisementTracking.loaded &&
    isIdleMapView &&
    !hasBlockingMapOverlay;
  const isAirportTripSoonTimerReady = isIdlePromotionTimerReady && onboardingPopupSteps === OnboardingPopupStepsEnum.Completed;
  const ageRestrictedPromotionLocation = useMemo(() => {
    const location = effectiveLocation ?? currentLocation ?? userLocation ?? serviceLocation;

    if (!location || location.isDefault || !Number.isFinite(location.lat) || !Number.isFinite(location.lng)) {
      return undefined;
    }

    return location;
  }, [currentLocation, effectiveLocation, serviceLocation, userLocation]);
  const hasNearbyDiscoverableAgeRestrictedMissions = useMemo(() => {
    if (!ageRestrictedPromotionLocation) {
      return false;
    }

    return [...(pinnedMissionCardProps ?? []), ...(missionCardProps ?? []), ...(flashMissionCardProps ?? [])].some((card) => {
      if (!card.hasAgeRestrictedMissions || !isMissionAvailableForDiscovery(card)) {
        return false;
      }

      const latitude = Number(card.geocoordinate?.y);
      const longitude = Number(card.geocoordinate?.x);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return false;
      }

      return getDistanceKm(ageRestrictedPromotionLocation, { lat: latitude, lng: longitude }) <= AGE_RESTRICTED_PROMOTION_RADIUS_KM;
    });
  }, [ageRestrictedPromotionLocation, pinnedMissionCardProps, missionCardProps, flashMissionCardProps]);
  const hasResolvedAirportPromotionPriority = !canFetchAirportMissions || airportMapQuery.isSuccess;
  const isAgeRestrictedMissionsTimerReady =
    isIdlePromotionTimerReady &&
    hasResolvedAirportPromotionPriority &&
    (airportMissionCardProps?.length ?? 0) <= 0 &&
    hasNearbyDiscoverableAgeRestrictedMissions;

  // Location-permission request step remains coordinated here with the location hook.
  useEffect(() => {
    if (!isMapPopupActive) {
      return;
    }
    // Allow one fresh auto-attempt each time the map regains focus (ex: back from OS settings).
    hasAutoNativePermissionAttemptRef.current = false;
  }, [isMapPopupActive]);

  useEffect(() => {
    if (onboardingPopupSteps !== OnboardingPopupStepsEnum.LocationPermissionRequest) {
      hasAutoNativePermissionAttemptRef.current = false;
      return;
    }

    if (!isMapPopupActive) {
      return;
    }

    // If location is already resolved, onboarding can continue without auto-prompting again.
    if (currentLocation && locationPermissionGranted) {
      return;
    }

    // Avoid automatic re-prompts after a deny; manual retries still work via overlay actions.
    if (hasAutoNativePermissionAttemptRef.current && !locationPermissionGranted) {
      return;
    }

    hasAutoNativePermissionAttemptRef.current = true;
    getUserCurrentLocation(false, false);
  }, [onboardingPopupSteps, isMapPopupActive, currentLocation, locationPermissionGranted]);

  // When map location resolves (GPS or fallback/default after permission denial),
  // advance onboarding so required profile/address gating can complete.
  useEffect(() => {
    if (!isMapPopupActive || onboardingPopupSteps !== OnboardingPopupStepsEnum.LocationPermissionRequest) {
      return;
    }

    const hasResolvedLocationForOnboarding =
      !waitingForBrowserLocation && !!currentLocation && locationPermissionStatus !== undefined;

    if (hasResolvedLocationForOnboarding) {
      setOnboardingPopupSteps(OnboardingPopupStepsEnum.ProfileInformationRequest);
    }
  }, [isMapPopupActive, onboardingPopupSteps, currentLocation, waitingForBrowserLocation, locationPermissionStatus]);

  useEffect(() => {
    if (layout.width < 1200) {
      dispatch(lightBoxActions.setMobile(true));
    } else {
      dispatch(lightBoxActions.setMobile(false));
    }
  }, [layout.width]);

  const resetLightBoxPresentationToDefault = () => {
    dispatch(lightBoxActions.setOverlayVisible(true));
    dispatch(lightBoxActions.setAllowBackgroundInteraction(false));
    dispatch(lightBoxActions.setContentVerticalAlign('center'));
    dispatch(lightBoxActions.setContentHorizontalAlign('center'));
    dispatch(lightBoxActions.setContentTopOffset(0));
    dispatch(lightBoxActions.setContentBottomOffset(0));
    dispatch(lightBoxActions.setContentLeftOffset(0));
  };

  const handleAirportTripSoonPopupClose = (dontShowAgain?: boolean) => {
    airportTripSoonPopupShownInSession = true;
    setAirportTripSoonPopupHandled(true);
    if (dontShowAgain) {
      advertisementTracking.dontShowAgain(AIRPORT_TRIP_SOON_POPUP_CODE);
    }
    resetLightBoxPresentationToDefault();
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setOnClose(undefined));
  };

  const handleAgeRestrictedMissionsPopupClose = (dontShowAgain?: boolean) => {
    if (!isCurrentMapPopupSession()) {
      return;
    }
    ageRestrictedMissionsPopupShownInSession = true;
    setAgeRestrictedMissionsPopupHandled(true);
    if (dontShowAgain) {
      advertisementTracking.dontShowAgain(AGE_RESTRICTED_MISSIONS_POPUP_CODE);
    }
    resetLightBoxPresentationToDefault();
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setOnClose(undefined));
  };

  const handleNoNearbyMissionsPopupClose = (dontShowAgain?: boolean) => {
    noNearbyMissionsPopupShownInSession = true;
    setNoNearbyMissionsPopupHandled(true);
    setShowNoNearbyMissionsPopup(false);
    setShowNoMissionOverlay(false);
    if (dontShowAgain) {
      advertisementTracking.dontShowAgain(NO_NEARBY_MISSIONS_POPUP_CODE);
    }
  };

  const handleViewFlashMissions = (dontShowAgain?: boolean) => {
    setMissionListFilter(FilterType.Flash);
    if (layout.width <= 1200) {
      setOpenListRequestId(openListRequestId + 1);
    }
    handleFlashMissionsClose(dontShowAgain);
  };

  const handleFlashMissionsClose = (dontShowAgain?: boolean) => {
    if (dontShowAgain) {
      advertisementTracking.dontShowAgain(FLASH_MISSIONS_ONBOARDING_CODE);
    }
    setOnboardingPopupSteps(OnboardingPopupStepsEnum.Completed);
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setOnClose(undefined));
  };

  const openAirportFlowFromBanner = (branchId: string, clearMapSelection: boolean = false) => {
    if (!branchId) {
      return;
    }

    const returnToListView = isWideLayout ? true : !!missionsStore.showMissionListView;

    resetLightBoxPresentationToDefault();
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setOnClose(undefined));
    dispatch(drawerActions.resetState());
    if (Platform.OS === 'web') {
      dispatch(myAccountActions.setVisibility(false));
    }
    dispatch(missionsActions.setSelectedBranch({ id: undefined, lat: undefined, lng: undefined }));

    setBranchMarker(undefined);
    setMissionFilter(undefined);
    setPreSelectedBranch(undefined);
    setSideBarState(SideBarState.List);
    setCleanSelection(clearMapSelection);
    if (!isWideLayout) {
      dispatch(missionsActions.setShowMissionListView(true));
      setOpenFilter(false);
    }
    setAirportStartupBookingRequest({
      branchId,
      key: StringHelper.generateRandomString(8),
      returnToListView,
    });
  };

  const handleAirportTripSoonPopupProceed = (branchId: string, dontShowAgain?: boolean) => {
    airportTripSoonPopupShownInSession = true;
    setAirportTripSoonPopupHandled(true);
    if (dontShowAgain) {
      advertisementTracking.dontShowAgain(AIRPORT_TRIP_SOON_POPUP_CODE);
    }
    openAirportFlowFromBanner(branchId, false);
  };

  const openAgeRestrictedMissionsList = () => {
    if (!isCurrentMapPopupSession()) {
      return;
    }
    resetLightBoxPresentationToDefault();
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setOnClose(undefined));
    dispatch(drawerActions.resetState());
    if (Platform.OS === 'web') {
      dispatch(myAccountActions.setVisibility(false));
    }
    dispatch(missionsActions.setSelectedBranch({ id: undefined, lat: undefined, lng: undefined }));
    setBranchMarker(undefined);
    setMissionFilter(undefined);
    setPreSelectedBranch(undefined);
    setSideBarState(SideBarState.List);
    setCleanSelection(true);
    setMissionListFilter(FilterType.AgeRestricted);
    if (!isWideLayout) {
      dispatch(missionsActions.setShowMissionListView(true));
      setOpenFilter(false);
    }
  };

  const handleAgeRestrictedMissionsPopupProceed = (dontShowAgain?: boolean) => {
    if (!isCurrentMapPopupSession()) {
      return;
    }
    ageRestrictedMissionsPopupShownInSession = true;
    setAgeRestrictedMissionsPopupHandled(true);
    if (dontShowAgain) {
      advertisementTracking.dontShowAgain(AGE_RESTRICTED_MISSIONS_POPUP_CODE);
    }
    openAgeRestrictedMissionsList();
  };

  const showLocationPermissionPopup = () => {
    if (!isCurrentMapPopupSession()) return;
    setShowedLocationPopup(false);
    dispatch(
      lightBoxActions.setContent(
        <LocationPermission
          onClose={() => {
            if (!isCurrentMapPopupSession()) return;
            setShowedLocationPopup(true);
            dispatch(lightBoxActions.setOnClose(undefined));
            dispatch(lightBoxActions.setVisibility(false));
          }}
        />,
      ),
    );
    dispatch(
      lightBoxActions.setOnClose(() => {
        if (!isCurrentMapPopupSession()) return;
        setShowedLocationPopup(true);
        dispatch(lightBoxActions.setVisibility(false));
        dispatch(lightBoxActions.setOnClose(undefined));
      }),
    );
    dispatch(lightBoxActions.setVisibility(true));
  };

  useEffect(() => {
    if (!isMapPopupActive || !noNearbyMissionsQuery.isSuccess) {
      return;
    }

    if (noNearbyMissionCount > 0) {
      setShowNoNearbyMissionsPopup(false);
      return;
    }

    if (
      noNearbyMissionsPopupHandled ||
      noNearbyMissionsPopupShownInSession ||
      !advertisementTracking.loaded ||
      !advertisementTracking.showAdvertisement(NO_NEARBY_MISSIONS_POPUP_CODE) ||
      onboardingPopupSteps !== OnboardingPopupStepsEnum.Completed ||
      isLightBoxVisible ||
      isSecondLightBoxVisible ||
      isModalVisible ||
      isMyAccountVisible
    ) {
      return;
    }

    noNearbyMissionsPopupShownInSession = true;
    setNoNearbyMissionsPopupHandled(true);
    setShowNoNearbyMissionsPopup(true);
  }, [
    isMapPopupActive,
    noNearbyMissionsQuery.isSuccess,
    noNearbyMissionCount,
    noNearbyMissionsPopupHandled,
    advertisementTracking.loaded,
    onboardingPopupSteps,
    isLightBoxVisible,
    isSecondLightBoxVisible,
    isModalVisible,
    isMyAccountVisible,
  ]);

  useEffect(() => {
    if (!isAirportTripSoonTimerReady || airportTripSoonPopupHandled || airportTripSoonPopupShownInSession) {
      return;
    }

    const timeout = setTimeout(() => {
      if (!isCurrentMapPopupSession()) return;
      setAirportTripSoonTimerElapsed(true);
    }, AIRPORT_TRIP_SOON_POPUP_DELAY_MS);

    return () => {
      clearTimeout(timeout);
    };
  }, [isAirportTripSoonTimerReady, airportTripSoonPopupHandled, isCurrentMapPopupSession]);

  useEffect(() => {
    if (
      !isAirportTripSoonTimerReady ||
      !airportTripSoonTimerElapsed ||
      airportTripSoonPopupHandled ||
      airportTripSoonPopupShownInSession
    ) {
      return;
    }

    const firstAirportBranchId = airportMissionCardProps?.[0]?.id;
    if (!firstAirportBranchId) {
      return;
    }

    airportTripSoonPopupShownInSession = true;
    setAirportTripSoonPopupHandled(true);

    if (!advertisementTracking.showAdvertisement(AIRPORT_TRIP_SOON_POPUP_CODE)) {
      return;
    }

    // Keep this 20s popup below the top logo bar and the notification/trial bar.
    const topBarBaseHeight = Platform.OS === 'android' ? 83 : 53;
    const topInfoBarHeight = 28;
    const airportTripPopupTopOffset = topBarBaseHeight + topInfoBarHeight + 49;

    dispatch(lightBoxActions.setOverlayVisible(false));
    dispatch(lightBoxActions.setAllowBackgroundInteraction(true));
    if (Platform.OS === 'web' && isWideLayout) {
      dispatch(lightBoxActions.setContentVerticalAlign('bottom'));
      dispatch(lightBoxActions.setContentHorizontalAlign('left'));
      dispatch(lightBoxActions.setContentTopOffset(0));
      dispatch(lightBoxActions.setContentBottomOffset(20));
      dispatch(lightBoxActions.setContentLeftOffset(20));
    } else {
      dispatch(lightBoxActions.setContentVerticalAlign('top'));
      dispatch(lightBoxActions.setContentHorizontalAlign('center'));
      dispatch(lightBoxActions.setContentTopOffset(airportTripPopupTopOffset));
      dispatch(lightBoxActions.setContentBottomOffset(0));
      dispatch(lightBoxActions.setContentLeftOffset(0));
    }
    dispatch(
      lightBoxActions.setContent(
        <AirportTripSoonPopup
          onClose={(dontShowAgain) => handleAirportTripSoonPopupClose(dontShowAgain)}
          onProceed={(dontShowAgain) => handleAirportTripSoonPopupProceed(firstAirportBranchId, dontShowAgain)}
        />,
      ),
    );
    dispatch(lightBoxActions.setVisibility(true));
  }, [
    isAirportTripSoonTimerReady,
    airportTripSoonTimerElapsed,
    airportTripSoonPopupHandled,
    airportMissionCardProps,
    userPrefs.uiLanguage,
    advertisementTracking.loaded,
    isWideLayout,
  ]);

  useEffect(() => {
    if (
      !isCurrentMapPopupSession() ||
      !isAgeRestrictedMissionsTimerReady ||
      ageRestrictedMissionsPopupHandled ||
      ageRestrictedMissionsPopupShownInSession
    ) {
      return;
    }

    const timeout = setTimeout(() => {
      if (!isCurrentMapPopupSession()) {
        return;
      }
      setAgeRestrictedMissionsTimerElapsed(true);
    }, AGE_RESTRICTED_MISSIONS_POPUP_DELAY_MS);

    return () => {
      clearTimeout(timeout);
    };
  }, [isAgeRestrictedMissionsTimerReady, ageRestrictedMissionsPopupHandled, isCurrentMapPopupSession]);

  useEffect(() => {
    if (
      !isCurrentMapPopupSession() ||
      !isAgeRestrictedMissionsTimerReady ||
      !ageRestrictedMissionsTimerElapsed ||
      ageRestrictedMissionsPopupHandled ||
      ageRestrictedMissionsPopupShownInSession
    ) {
      return;
    }

    ageRestrictedMissionsPopupShownInSession = true;
    setAgeRestrictedMissionsPopupHandled(true);

    if (!advertisementTracking.showAdvertisement(AGE_RESTRICTED_MISSIONS_POPUP_CODE)) {
      return;
    }

    const topBarBaseHeight = Platform.OS === 'android' ? 83 : 53;
    const topInfoBarHeight = 28;
    const popupTopOffset = topBarBaseHeight + topInfoBarHeight + 49;

    dispatch(lightBoxActions.setOverlayVisible(false));
    dispatch(lightBoxActions.setAllowBackgroundInteraction(true));
    if (Platform.OS === 'web' && isWideLayout) {
      dispatch(lightBoxActions.setContentVerticalAlign('bottom'));
      dispatch(lightBoxActions.setContentHorizontalAlign('left'));
      dispatch(lightBoxActions.setContentTopOffset(0));
      dispatch(lightBoxActions.setContentBottomOffset(20));
      dispatch(lightBoxActions.setContentLeftOffset(20));
    } else {
      dispatch(lightBoxActions.setContentVerticalAlign('top'));
      dispatch(lightBoxActions.setContentHorizontalAlign('center'));
      dispatch(lightBoxActions.setContentTopOffset(popupTopOffset));
      dispatch(lightBoxActions.setContentBottomOffset(0));
      dispatch(lightBoxActions.setContentLeftOffset(0));
    }
    dispatch(
      lightBoxActions.setContent(
        <AgeRestrictedMissionsPopup
          onClose={(dontShowAgain) => handleAgeRestrictedMissionsPopupClose(dontShowAgain)}
          onProceed={(dontShowAgain) => handleAgeRestrictedMissionsPopupProceed(dontShowAgain)}
        />,
      ),
    );
    dispatch(lightBoxActions.setVisibility(true));
  }, [
    isAgeRestrictedMissionsTimerReady,
    ageRestrictedMissionsTimerElapsed,
    ageRestrictedMissionsPopupHandled,
    userPrefs.uiLanguage,
    advertisementTracking.loaded,
    isCurrentMapPopupSession,
  ]);

  const setOpenMapAsBoudaries = (expansionAmount: number, position: MapLocation) => {
    setAppliedTopLeftLocation(`${position.lat + expansionAmount * 1.5}&${position.lng - expansionAmount}`);
    setAppliedBottomRightLocation(`${position.lat - expansionAmount * 1.5}&${position.lng + expansionAmount}`);
  };

  useFocusEffect(
    React.useCallback(() => {
      if (authStore.logged === LoggedState.Logged || authStore.logged === LoggedState.Anonymous) {
        //console.log('navigation end map');
        const hasRouteSelectedBranch = hasMapRouteSelection({
          pos: route?.params?.['pos'],
          location: route?.params?.['location'],
        });
        const hasAirportOpenParams = !!(route?.params?.['airportPopupKey'] || route?.params?.['airportOpenStep'] || route?.params?.['airportOpenSource']);
        const shouldReinitializeMapState = !hasFocusedMapRef.current || hasRouteSelectedBranch || hasAirportOpenParams;

        if (shouldReinitializeMapState) {
          setAwaitingAndroidInitialViewportResults(Platform.OS === 'android' && !hasRouteSelectedBranch);
          hasCenteredOnEffectiveLocationRef.current = false;
          setLastMissionsViewCenter(undefined);
          setLastMissionsViewZoom(undefined);
          lastSmartZoomQueryTsRef.current = 0;
          clearMarkers();
          setAutoZoomOnFirstLoad(!hasRouteSelectedBranch);
          setSuppressSearchAreaDuringInitialZoom(!hasRouteSelectedBranch);
          setMapZoom(hasRouteSelectedBranch ? LIST_SELECTION_ZOOM : INITIAL_ZOOM_WIDE_AREA);
          setMapMode(hasRouteSelectedBranch ? MapMode.USER_VIEWPORT_MODE : MapMode.INITIAL_LOAD);
          setShowNoMissionOverlay(false);
          if (!hasRouteSelectedBranch) {
            setTopLeftLocation(undefined);
            setBottomRightLocation(undefined);
            setAppliedTopLeftLocation(undefined);
            setAppliedBottomRightLocation(undefined);
          }
          const seed = StringHelper.generateRandomString(8);
          setMissionQuerySeed(seed);
          dispatch(missionsActions.setReloadMissionListSeed(seed));
        }

        AnalyticsHelper.logScreenView(route.name).then(() => {
          //console.log('Analytics', 'Page view: Map');
        });

        if (shouldReinitializeMapState) {
          if (route?.params && route?.params['pos'] && route?.params['location']) {
            const mapLocation = {
              lat: parseFloat((route?.params['location'] as string).split('|')[0]),
              lng: parseFloat((route?.params['location'] as string).split('|')[1]),
            };
            const airportPopupKey = typeof route?.params?.['airportPopupKey'] === 'string' ? (route?.params['airportPopupKey'] as string) : undefined;
            const airportOpenStep = route?.params?.['airportOpenStep'] === 'routeForm' ? ('routeForm' as const) : ('routeForm' as const);
            const airportOpenSource = route?.params?.['airportOpenSource'] === 'banner' ? ('banner' as const) : ('takeMorePopup' as const);

            cancelPendingViewportSearch();
            listMapBoundsRef.current = undefined;
            restoreViewAfterMapReadyRef.current = false;
            ignoreNextBoundsFromRestoreRef.current = false;
            userHasPannedSinceProgrammaticMoveRef.current = false;
            setLastMissionsViewCenter(mapLocation);
            setLastMissionsViewZoom(LIST_SELECTION_ZOOM);
            suppressProgrammaticInteraction();
            setViewportCommand({
              id: Date.now(),
              type: 'centerZoom',
              center: mapLocation,
              zoom: LIST_SELECTION_ZOOM,
              suppressBoundsCallback: true,
            });
            setPreSelectedBranch({ id: route?.params['pos'], lat: mapLocation.lat, lng: mapLocation.lng });
            handleMarkerClickCallBack(route?.params['pos'], mapLocation.lat, mapLocation.lng);
            if (airportPopupKey) {
              setAirportBannerPopupRequest({
                branchId: route?.params['pos'] as string,
                key: airportPopupKey,
                openStep: airportOpenStep,
                source: airportOpenSource,
              });
              navigation.setParams({ airportPopupKey: undefined, airportOpenStep: undefined, airportOpenSource: undefined } as any);
            }

            // Keep using the effective location from the hook; we still
            // propagate this explicit mapLocation to the map bounds below.
            setOpenMapAsBoudaries(0.15, mapLocation);
            getUserCurrentLocation(true, true);
          } else {
            getUserCurrentLocation(false, false);
          }

          setRefreshMap(true);
        }
        hasFocusedMapRef.current = true;
        dispatch(navBarActions.setVisibility(true));
        dispatch(missionsActions.setReloadRejectedValidationSeed(StringHelper.generateRandomString()));
      }
    }, [authStore.logged, route.params]),
  );

  useFocusEffect(
    React.useCallback(() => {
      return () => {
        resetLightBoxPresentationToDefault();
        dispatch(lightBoxActions.setOnClose(undefined));
        dispatch(lightBoxActions.setContent(<></>));
        dispatch(lightBoxActions.setVisibility(false));
        setAirportTripSoonTimerElapsed(false);
        setAirportTripSoonPopupHandled(false);
        airportTripSoonPopupShownInSession = false;
      };
    }, [dispatch]),
  );

  useEffect(() => {
    if (authStore.logged !== LoggedState.NotLogged) {
      return;
    }

    setAirportTripSoonTimerElapsed(false);
    setAirportTripSoonPopupHandled(false);
    airportTripSoonPopupShownInSession = false;
    setShowNoNearbyMissionsPopup(false);
    setNoNearbyMissionsPopupHandled(false);
    noNearbyMissionsPopupShownInSession = false;
    setShowedLocationPopup(false);
    setDismissLocation(false);
  }, [authStore.logged]);

  useEffect(() => {
    setMissionQuerySeed(missionsStore.missionListSeed);
  }, [missionsStore.missionListSeed]);

  useEffect(() => {
    if (route) {
      dispatch(navBarActions.setPath(route.name));
    }
  }, [route]);

  useEffect(() => {
    if (missionsStore.resetMap) {
      const shouldClearSelection = shouldClearMapSelectionForReset(true, {
        pos: route?.params?.['pos'],
        location: route?.params?.['location'],
      });
      if (shouldClearSelection) {
        setSideBarState(SideBarState.List);
        setCleanSelection(false);
        setMissionFilter(undefined);
        setBranchMarker(undefined);

        dispatch(missionsActions.setSelectedBranch({ id: undefined, lat: undefined, lng: undefined }));
        dispatch(drawerActions.resetState());
      }

      dispatch(missionsActions.setResetMap(false));
    }
  }, [dispatch, missionsStore.resetMap, route?.params]);

  useEffect(() => {
    if (!airportMapQuery.isSuccess) {
      return;
    }
  }, [airportMapQuery.isSuccess]);

  useEffect(() => {
    if (!autoZoomOnFirstLoad || !mapQuery.isSuccess || mapQuery.isPreviousData || preSelectedBranch?.id || branchMarker) return;
    if (mapQuery.dataUpdatedAt === lastSmartZoomQueryTsRef.current) return;
    lastSmartZoomQueryTsRef.current = mapQuery.dataUpdatedAt;
    const center = effectiveLocation ?? userLocation ?? currentLocation ?? fallBackLocation;
    const allMissions = [...(mapQuery.data?.missions ?? []), ...(mapQuery.data?.lowOrderMissions ?? []), ...(mapQuery.data?.highOrderMissions ?? [])];
    const bounds = getInitialDiscoveryBounds(center, allMissions);
    userHasPannedSinceProgrammaticMoveRef.current = false;
    applyNextProgrammaticBoundsRef.current = true;
    setLastMissionsViewCenter(center);
    suppressProgrammaticInteraction(2200);
    setViewportCommand({
      id: Date.now(), type: 'fitBounds', ...bounds,
      edgePadding: { top: isWideLayout ? 48 : 180, right: 56, bottom: 80, left: 48 },
    });
    setShowNoMissionOverlay(allMissions.length === 0);
    setMapMode(MapMode.USER_VIEWPORT_MODE);
    setAutoZoomOnFirstLoad(false);
  }, [autoZoomOnFirstLoad, mapQuery.isSuccess, mapQuery.isPreviousData, mapQuery.dataUpdatedAt,
    preSelectedBranch?.id, branchMarker, effectiveLocation, userLocation, currentLocation, fallBackLocation, isWideLayout]);

  useEffect(() => {
    if (!mapQuery.isSuccess || mapMode !== MapMode.USER_VIEWPORT_MODE) {
      return;
    }
    const hasAnyMissionInCurrentBounds = [mapQuery.data?.missions ?? [], mapQuery.data?.lowOrderMissions ?? [], mapQuery.data?.highOrderMissions ?? []]
      .flat()
      .some((m) => {
        const latitude = Number(m.geocoordinate?.y);
        const longitude = Number(m.geocoordinate?.x);
        return Number.isFinite(latitude) && Number.isFinite(longitude);
      });
    setShowNoMissionOverlay(!hasAnyMissionInCurrentBounds);
  }, [mapMode, mapQuery.isSuccess, mapQuery.data, mapQuery.dataUpdatedAt]);

  useEffect(() => {
    if (!suppressSearchAreaDuringInitialZoom) {
      return;
    }

    if (autoZoomOnFirstLoad) {
      return;
    }

    const boundsSettled =
      !!topLeftLocation && !!bottomRightLocation && topLeftLocation === appliedTopLeftLocation && bottomRightLocation === appliedBottomRightLocation;

    if (boundsSettled) {
      setSuppressSearchAreaDuringInitialZoom(false);
    }
  }, [suppressSearchAreaDuringInitialZoom, autoZoomOnFirstLoad, topLeftLocation, bottomRightLocation, appliedTopLeftLocation, appliedBottomRightLocation]);

  useEffect(() => {
    if (preSelectedBranch == undefined && Platform.OS !== 'web') {
      dispatch(missionsActions.setSelectedBranch({ id: undefined, lat: undefined, lng: undefined }));
    }
  }, [preSelectedBranch]);

  useEffect(() => {
    if (preSelectedBranch?.id && !branchMarker && markers?.length > 0) {
      handleMarkerClickCallBack(preSelectedBranch.id, preSelectedBranch.lat, preSelectedBranch.lng);
    }
  }, [preSelectedBranch?.id, preSelectedBranch?.lat, preSelectedBranch?.lng, branchMarker, markers?.length]);

  const requestLocationPermission = (skipGetMissions: boolean = false, overridePreselectedBranch: boolean = false) => {
    if (!isCurrentMapPopupSession()) return;
    Location.requestForegroundPermissionsAsync().then(
      (result) => {
        if (!isCurrentMapPopupSession()) return;
        if (!result.granted) {
          setLocationPermissionState(result.status === 'undetermined' ? LocationPermissionStatus.Undetermined : LocationPermissionStatus.Denied, false, result.canAskAgain);
          useDefaultLocation();
          return;
        }

        setLocationPermissionState(LocationPermissionStatus.Granted, true, result.canAskAgain);
        internalGetUserCurrentLocation(skipGetMissions, overridePreselectedBranch, showLocationPermissionPopup);
      },
      () => {
        if (!isCurrentMapPopupSession()) return;
        setLocationPermissionState(LocationPermissionStatus.Denied, false, false);
        useDefaultLocation();
      },
    );
  };

  const resolveNativePermissionAndGetLocation = (skipGetMissions: boolean, overridePreselectedBranch: boolean) => {
    if (!isCurrentMapPopupSession()) return;
    Location.getForegroundPermissionsAsync()
      .then((permissionResult) => {
        if (!isCurrentMapPopupSession()) return;
        if (permissionResult.granted) {
          setLocationPermissionState(LocationPermissionStatus.Granted, true, permissionResult.canAskAgain);
          internalGetUserCurrentLocation(skipGetMissions, overridePreselectedBranch, showLocationPermissionPopup);
          return;
        }

        if (permissionResult.status === 'undetermined') {
          setLocationPermissionState(LocationPermissionStatus.Undetermined, false, permissionResult.canAskAgain);
          if (permissionResult.canAskAgain && !showedLocationPopup) {
            showLocationPermissionPopup();
            return;
          }
        } else {
          setLocationPermissionState(LocationPermissionStatus.Denied, false, permissionResult.canAskAgain);
        }

        requestLocationPermission(skipGetMissions, overridePreselectedBranch);
      })
      .catch(() => {
        if (!isCurrentMapPopupSession()) return;
        setLocationPermissionState(LocationPermissionStatus.Denied, false, false);
        useDefaultLocation();
      });
  };

  useEffect(() => {
    if (showedLocationPopup) {
      if (Platform.OS === 'web') {
        getUserCurrentLocation(false, false);
        return;
      }

      requestLocationPermission();
    }
  }, [showedLocationPopup]);

  const getUserCurrentLocation = (skipGetMissions: boolean, overridePreselectedBranch: boolean) => {
    if (!isCurrentMapPopupSession()) return;
    if (Platform.OS !== 'web') {
      resolveNativePermissionAndGetLocation(skipGetMissions, overridePreselectedBranch);
      return;
    }

    if (Platform.OS === 'web' && !showedLocationPopup) {
      const webNavigator = navigator as any;
      const permissionsApi = webNavigator?.permissions;
      if (permissionsApi?.query) {
        permissionsApi
          .query({ name: 'geolocation' })
          .then((permissionStatus: any) => {
            if (!isCurrentMapPopupSession()) return;
            if (permissionStatus?.state === 'granted') {
              internalGetUserCurrentLocation(skipGetMissions, overridePreselectedBranch, showLocationPermissionPopup);
            } else {
              showLocationPermissionPopup();
            }
          })
          .catch(() => {
            if (!isCurrentMapPopupSession()) return;
            showLocationPermissionPopup();
          });
      } else {
        showLocationPermissionPopup();
      }
      return;
    }

    internalGetUserCurrentLocation(skipGetMissions, overridePreselectedBranch, showLocationPermissionPopup);
  };

  useEffect(() => {
    if (triggerGetUserLocation) {
      getUserCurrentLocation(false, true);
      setTriggerGetUserLocation(false);
    }
  }, [triggerGetUserLocation]);

  const resetMapAfterLocationChange = (center?: MapLocation) => {
    resetMapToInitialLoad(center ?? userLocation ?? serviceLocation ?? fallBackLocation);
    setRefreshMap(true);
  };

  useEffect(() => {
    // A late GPS/service fallback must not replace an explicitly opened mission.
    if (branchMarker || preSelectedBranch?.id || hasMapRouteSelection({
      pos: route?.params?.['pos'],
      location: route?.params?.['location'],
    })) {
      return;
    }

    const currentLocationKey = getMapLocationKey(currentLocation);
    const serviceLocationKey = getMapLocationKey(serviceLocation);

    const shouldRecenterOnServiceFallback =
      isMapFocused &&
      !!serviceLocationKey &&
      !!currentLocationKey &&
      currentLocationKey === serviceLocationKey &&
      !currentLocation?.isDefault &&
      !locationPermissionGranted;

    if (!shouldRecenterOnServiceFallback) {
      if (hasGpsFix || locationPermissionGranted || currentLocation?.isDefault || !serviceLocationKey) {
        serviceFallbackRecenterKeyRef.current = undefined;
      }
      return;
    }

    if (serviceFallbackRecenterKeyRef.current === serviceLocationKey) {
      return;
    }

    serviceFallbackRecenterKeyRef.current = serviceLocationKey;
    resetMapAfterLocationChange(currentLocation);
  }, [currentLocation, serviceLocation, locationPermissionGranted, isMapFocused, hasGpsFix,
    branchMarker, preSelectedBranch?.id, route?.params?.['pos'], route?.params?.['location']]);

  const handleMapShowList = (showList: boolean) => {
    dispatch(missionsActions.setShowMissionListView(showList));

    if (showList) {
      listMapBoundsRef.current = activeBounds;
      setOpenFilter(false);
      restoreViewAfterMapReadyRef.current = false;
    } else {
      // Only web needs an explicit map reinitialization on list->map.
      if (Platform.OS === 'web') {
        setRefreshMap(true);
        if (lastMissionsViewCenter !== undefined || listMapBoundsRef.current) {
          restoreViewAfterMapReadyRef.current = true;
        }
      }
    }
  };

  useEffect(() => {
    if (missionsStore.showMissionListView && !isWideLayout) {
      setOpenFilter(false);
    }
  }, [missionsStore.showMissionListView, isWideLayout]);

  useEffect(() => {
    if (!showMobileListView) {
      setIsAirportBookingVisibleOnMobile(false);
    }
  }, [showMobileListView]);

  useEffect(() => {
    if (!isWideLayout && openListRequestId > 0) {
      handleMapShowList(true);
    }
  }, [isWideLayout, openListRequestId]);

  const handleRegularMissionFilterChange = (filter: FilterType) => {
    setMissionListFilter(filter);
    updateMarkers((selectedFilterItems ?? []).map((x) => x.id), filter);
  };

  const handleFeaturedMissionsPress = () => {
    handleRegularMissionFilterChange(FilterType.Featured);
    setCompactFiltersExpanded(true);
    handleMapShowList(true);
  };

  const cancelOngoingMapRequests = () => {
    void queryClient.cancelQueries({ queryKey: ['customMap', 'missions'] });
    void queryClient.cancelQueries({ queryKey: ['customMap', 'airportMissions'] });
  };

  const handleMissionListRefresh = async () => {
    if (!canFetchMapMissions && !canFetchAirportMissions) {
      return;
    }

    const refreshPromises: Promise<unknown>[] = [];
    if (canFetchMapMissions) {
      refreshPromises.push(mapQuery.refetch());
    }
    if (canFetchAirportMissions) {
      refreshPromises.push(airportMapQuery.refetch());
    }

    await Promise.allSettled(refreshPromises);
  };

  const handleAirportBannerPress = (branchId: string) => {
    openAirportFlowFromBanner(branchId, false);
  };

  const handleSearchThisAreaPress = () => {
    if (!topLeftLocation || !bottomRightLocation) {
      return;
    }

    cancelOngoingMapRequests();
    userHasPannedSinceProgrammaticMoveRef.current = true;
    setAutoZoomOnFirstLoad(false);
    setMapMode(MapMode.USER_VIEWPORT_MODE);
    setShowNoMissionOverlay(false);
    setAppliedTopLeftLocation(topLeftLocation);
    setAppliedBottomRightLocation(bottomRightLocation);
  };

  const handleFilters = (filters: string[]) => {
    setSelectedFilterItems(
      (filters ?? []).map((id) => {
        const selectedFilter = filterItems.find((x) => `${x.id}` === `${id}`);
        return {
          id: `${id}`,
          name: selectedFilter?.name ?? `${id}`,
          highlight: false,
          checked: true,
        };
      }),
    );
    setSideBarState(SideBarState.List);
    updateMarkers(filters, missionListFilter);
  };

  const mapReferencePosition = useMemo<MapLocation>(() => {
    if (lastMissionsViewCenter) {
      return lastMissionsViewCenter;
    }

    if (preSelectedBranch?.lat !== undefined && preSelectedBranch?.lng !== undefined) {
      return { lat: preSelectedBranch.lat, lng: preSelectedBranch.lng };
    }

    // Avoid briefly centering on service address while device GPS is still resolving.
    if (waitingForBrowserLocation) {
      return fallBackLocation;
    }

    return effectiveLocation ?? fallBackLocation;
  }, [lastMissionsViewCenter, preSelectedBranch?.lat, preSelectedBranch?.lng, waitingForBrowserLocation, effectiveLocation, fallBackLocation]);
  const mapUserLocation = useMemo<MapLocation | undefined>(() => {
    if (!hasGpsFix) {
      return undefined;
    }

    return userLocation ?? currentLocation;
  }, [hasGpsFix, userLocation, currentLocation]);
  const mapPanToOriginPosition = useMemo<MapLocation>(
    () => mapUserLocation ?? currentLocation ?? serviceLocation ?? effectiveLocation ?? fallBackLocation,
    [mapUserLocation, currentLocation, serviceLocation, effectiveLocation, fallBackLocation],
  );
  const handleMapPanToOrigin = useCallback(() => {
    restoreViewAfterMapReadyRef.current = false;
    ignoreNextBoundsFromRestoreRef.current = false;
    preserveAirportFlowOnNextCleanSelectionRef.current = false;
    userHasPannedSinceProgrammaticMoveRef.current = false;
    setLastMissionsViewCenter(undefined);
    setLastMissionsViewZoom(undefined);
    setMissionFilter(undefined);
    setSideBarState(SideBarState.List);
    setBranchMarker(undefined);
    setPreSelectedBranch(undefined);
    setCleanSelection(true);
    dispatch(missionsActions.setSelectedBranch({ id: undefined, lat: undefined, lng: undefined }));
    dispatch(drawerActions.resetState());
    clearMapSelectionRouteParams();
    lastSmartZoomQueryTsRef.current = 0;
    resetMapToInitialLoad(mapPanToOriginPosition);
  }, [
    resetMapToInitialLoad,
    mapPanToOriginPosition,
    clearMapSelectionRouteParams,
    dispatch,
    ignoreNextBoundsFromRestoreRef,
    restoreViewAfterMapReadyRef,
    setBranchMarker,
    setLastMissionsViewCenter,
    setLastMissionsViewZoom,
    setPreSelectedBranch,
    setSideBarState,
    userHasPannedSinceProgrammaticMoveRef,
  ]);
  const shouldGateAndroidInitialMissionPreview = Platform.OS === 'android' && awaitingAndroidInitialViewportResults;
  const shouldShowInitialMapLoadingOverlay = shouldShowMapMissionLoading || shouldGateAndroidInitialMissionPreview;
  const selectedCircuitMission = missions?.find((mission) => mission.branchId === branchMarker && mission.isCompetitorCircuit);
  const competitorPreviewMarkers = useMemo<MapMarker[]>(() => (selectedCircuitMission?.competitorLocations ?? [])
    .filter((location) => location.geocoordinate?.x !== undefined && location.geocoordinate?.y !== undefined)
    .map((location) => ({
      id: `competitor-preview-${location.competitorBranchId}`,
      title: `${location.name} — ${location.address}`,
      coordinate: { latitude: location.geocoordinate!.y!, longitude: location.geocoordinate!.x! },
      selected: false,
      isCompetitorPreview: true,
      competitorCircuitRole: 'competitor',
      svgIcon: competitorCircuitCompetitorIcon,
      faIcon: undefined,
      seed: `${location.competitorBranchId!}:competitor-circuit-competitor`,
      defaultPin: 'default',
      onClick: () => undefined,
    })), [selectedCircuitMission?.competitorLocations]);
  const markerItemsForMap = useMemo(() => shouldGateAndroidInitialMissionPreview
    ? []
    : selectedCircuitMission ? [
        ...markers.filter((marker) => marker.id === branchMarker),
        ...competitorPreviewMarkers,
      ] : markers,
    [shouldGateAndroidInitialMissionPreview, selectedCircuitMission, markers, branchMarker, competitorPreviewMarkers]);
  const initialPreviewHasRenderableData =
    !shouldShowMapMissionLoading &&
    ((markers?.length ?? 0) > 0 ||
      (missionCardProps?.length ?? 0) > 0 ||
      (pinnedMissionCardProps?.length ?? 0) > 0 ||
      (flashMissionCardProps?.length ?? 0) > 0 ||
      (airportMissionCardProps?.length ?? 0) > 0 ||
      (airportMapGroups?.length ?? 0) > 0);
  const initialQueriesSettledWithoutData =
    !shouldShowMapMissionLoading &&
    !autoZoomOnFirstLoad &&
    mapQuery.isFetched &&
    !mapQuery.isFetching &&
    (!canFetchAirportMissions || airportMapQuery.isFetched) &&
    (!canFetchAirportMissions || !airportMapQuery.isFetching) &&
    (markers?.length ?? 0) === 0 &&
    (missionCardProps?.length ?? 0) === 0 &&
    (pinnedMissionCardProps?.length ?? 0) === 0 &&
    (flashMissionCardProps?.length ?? 0) === 0 &&
    (airportMissionCardProps?.length ?? 0) === 0 &&
    (airportMapGroups?.length ?? 0) === 0;

  useEffect(() => {
    if (!awaitingAndroidInitialViewportResults) {
      return;
    }

    if (mapQuery.isError) {
      setAwaitingAndroidInitialViewportResults(false);
      return;
    }

    if (hasLoadedViewportMapResults) {
      setAwaitingAndroidInitialViewportResults(false);
      return;
    }

    if (initialPreviewHasRenderableData || initialQueriesSettledWithoutData) {
      setAwaitingAndroidInitialViewportResults(false);
      return;
    }

    const settledWithoutViewportRefetch =
      mapQuery.isSuccess && !autoZoomOnFirstLoad && !suppressSearchAreaDuringInitialZoom && !hasViewportBounds && !shouldShowMapMissionLoading;

    if (settledWithoutViewportRefetch) {
      setAwaitingAndroidInitialViewportResults(false);
    }
  }, [
    awaitingAndroidInitialViewportResults,
    mapQuery.isError,
    hasLoadedViewportMapResults,
    initialPreviewHasRenderableData,
    initialQueriesSettledWithoutData,
    mapQuery.isSuccess,
    mapQuery.isFetched,
    mapQuery.isFetching,
    airportMapQuery.isFetched,
    airportMapQuery.isFetching,
    autoZoomOnFirstLoad,
    suppressSearchAreaDuringInitialZoom,
    hasViewportBounds,
    shouldShowMapMissionLoading,
    markers,
    missionCardProps,
    pinnedMissionCardProps,
    flashMissionCardProps,
    airportMissionCardProps,
    airportMapGroups,
    canFetchAirportMissions,
  ]);

  return (
    <AuthGuard>
      {true && (
        <View style={responsiveStyles('container')}>
          {showOnARollPopup && (
            <OnARollPopup
              onClose={() => {
                dismissOnARollPopup();
              }}
              onMissionSelect={(missions) => {
                navigation.navigate({
                  name: 'map',
                  params: { pos: missions.branchId, location: `${missions.geocoordinate.y}|${missions.geocoordinate.x}` },
                } as never);

                dismissOnARollPopup();
              }}
            />
          )}
          <NoNearbyMissionsPopup
            visible={isMapPopupActive && showNoNearbyMissionsPopup}
            onClose={handleNoNearbyMissionsPopupClose}
          />
          <Modal animationType="none" visible={isMapPopupActive && onboardingPopupSteps === OnboardingPopupStepsEnum.EmailVerified} transparent={true}>
            <Overlay showOverLay={true} zIndex={3000}>
              <Popup>
                <AccountVerificationPopup
                  onClose={() => {
                    setOnboardingPopupSteps(OnboardingPopupStepsEnum.Onboarding);
                  }}
                />
              </Popup>
            </Overlay>
          </Modal>
          <MobileAppCallToAction />
          <View style={responsiveStyles('mapContainer')}>
            {/* Tap-outside-to-dismiss layer for the filter popup.
                  Avoid wrapping the whole screen in a Touchable to prevent gesture conflicts with MapView (pinch/zoom). */}
            {showFilters && openFilter ? (
              <Pressable onPress={() => setOpenFilter(false)} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2000 }} />
            ) : null}
            {locationPermissionStatus === LocationPermissionStatus.Denied &&
              !dismissLocation &&
              !askLocation &&
              currentLocation &&
              !locationPermissionGranted &&
              !waitingForBrowserLocation && (
                <Pressable
                  style={styles.permissionOverlay}
                  onPress={() => {
                    if (Platform.OS === 'web') {
                      getUserCurrentLocation(false, false);
                    } else if (locationPermissionCanAskAgain) {
                      getUserCurrentLocation(false, false);
                    } else {
                      Linking.openSettings();
                    }
                  }}
                >
                  <MoreInfoButton title={''} size={16} color="white" style={{ marginRight: 8 }} openURL={'location-lanla-website-support-link'}>
                    {`${t('help.location-support-retry')}`}
                  </MoreInfoButton>
                  <Label variant={LabelType.titleWhiteSmall}>{t('not_allowed_location')}</Label>
                  <Pressable
                    onPress={() => {
                      setDismissLocation(true);
                    }}
                    style={{ marginLeft: 8, alignSelf: 'center' }}
                  >
                    <FontAwesomeIcon icon={faXmark} size={16} style={styles['permissionOverlay.help.icon']} />
                  </Pressable>
                </Pressable>
              )}
            {locationPermissionStatus === LocationPermissionStatus.Undetermined &&
              !dismissLocation &&
              !askLocation &&
              currentLocation &&
              !locationPermissionGranted &&
              !waitingForBrowserLocation && (
                <Pressable
                  style={styles.permissionOverlay}
                  onPress={() => {
                    if (Platform.OS === 'web') {
                      getUserCurrentLocation(false, false);
                    } else if (locationPermissionCanAskAgain) {
                      getUserCurrentLocation(false, false);
                    } else {
                      Linking.openSettings();
                    }
                  }}
                >
                  <MoreInfoButton title={''} size={16} color="white" style={{ marginRight: 8 }} openURL={'location-lanla-website-support-link'}>
                    {`${t('help.location-support-retry')}`}
                  </MoreInfoButton>
                  <Label variant={LabelType.titleWhiteSmall}>{t('not_able_location_information')}</Label>
                  <Pressable
                    onPress={() => {
                      setDismissLocation(true);
                    }}
                    style={{ marginLeft: 8, alignSelf: 'center' }}
                  >
                    <FontAwesomeIcon icon={faXmark} size={16} style={styles['permissionOverlay.help.icon']} />
                  </Pressable>
                </Pressable>
              )}
            {!dismissLocation && showFilters && !waitingForBrowserLocation && !userLocation && locationPermissionGranted && (
              <Pressable
                style={styles.permissionOverlay}
                onPress={() => {
                  if (Platform.OS === 'web') {
                    window.location.reload();
                  } else {
                    getUserCurrentLocation(false, false);
                  }
                }}
              >
                <MoreInfoButton title={''} size={16} color="white" style={{ marginRight: 8 }} openURL={'location-lanla-website-support-link'}>
                  {`${t('help.location-support-retry')}`}
                </MoreInfoButton>
                <Label style={{ flexShrink: 1 }} variant={LabelType.titleWhiteSmall}>
                  {t('location_retry')}
                </Label>
                <Pressable
                  onPress={() => {
                    setDismissLocation(true);
                  }}
                  style={{ marginLeft: 8, alignSelf: 'center' }}
                >
                  <FontAwesomeIcon icon={faXmark} size={16} style={styles['permissionOverlay.help.icon']} />
                </Pressable>
              </Pressable>
            )}
            {!dismissLocation && showFilters && waitingForBrowserLocation && (
              <View style={styles.permissionOverlay}>
                <MoreInfoButton title={''} size={16} color="white" style={{ marginRight: 8 }} openURL={'location-lanla-website-support-link'}>
                  {`${t('help.location-support-retry')}`}
                </MoreInfoButton>
                <Label numberOfLines={3} variant={LabelType.titleWhiteSmall}>
                  {t('looking_for_position')}
                </Label>
                <Pressable
                  onPress={() => {
                    setDismissLocation(true);
                  }}
                  style={{ marginLeft: 8, alignSelf: 'center' }}
                >
                  <FontAwesomeIcon icon={faXmark} size={16} style={styles['permissionOverlay.help.icon']} />
                </Pressable>
              </View>
            )}
            {!effectiveLocation && (
              <View style={styles.permissionOverlay}>
                <MoreInfoButton title={''} size={16} color="white" style={{ marginRight: 8 }} openURL={'location-lanla-website-support-link'}>
                  {`${t('help.location-support-retry')}`}
                </MoreInfoButton>
                <Label numberOfLines={3} variant={LabelType.titleWhiteSmall}>
                  {t('check-location-permission')}
                </Label>
              </View>
            )}
            {!showMobileListView && shouldShowInitialMapLoadingOverlay && (
              <View
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 2150,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                pointerEvents="box-none"
              >
                <View
                  style={{
                    backgroundColor: 'rgba(0,0,0,0.75)',
                    borderRadius: 10,
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    flexDirection: 'row',
                    alignItems: 'center',
                    maxWidth: '80%',
                  }}
                >
                  <ActivityIndicator size="small" color="rgba(255,255,255,0.9)" style={{ marginRight: 8 }} />
                  <Label variant={LabelType.titleWhiteSmall}>{t('loading')}...</Label>
                </View>
              </View>
            )}
            {(Platform.OS === 'web' ? !showMobileListView : true) && (
              <Map
                mapId="missions-map"
                refreshMap={refreshMap}
                onMapRefreshed={() => {
                  setRefreshMap(false);
                  if (Platform.OS === 'web' && restoreViewAfterMapReadyRef.current && (lastMissionsViewCenter || listMapBoundsRef.current)) {
                    restoreViewAfterMapReadyRef.current = false;
                    ignoreNextBoundsFromRestoreRef.current = true;
                    const center = lastMissionsViewCenter;
                    const zoom = lastMissionsViewZoom ?? mapZoom;
                    // Delay so map has layout/size before we apply center+zoom (fixes zoomed-out restore on web and mobile).
                    setTimeout(() => {
                      setViewportCommand({
                        id: Date.now(),
                        ...(listMapBoundsRef.current
                          ? { type: 'fitBounds' as const, ...listMapBoundsRef.current, padding: 0 }
                          : { type: 'centerZoom' as const, center, zoom }),
                      });
                    }, 250);
                  }
                }}
                hidePanToOrigin={isAirportBookingVisibleOnMobile || showMobileListView}
                showUserLocation={locationPermissionGranted}
                referencePosition={mapReferencePosition}
                panToOriginPosition={mapPanToOriginPosition}
                userLocationPosition={mapUserLocation}
                markerItems={markerItemsForMap}
                markerClick={handleMapMarkerSelect}
                cleanSelection={cleanSelection}
                selectedId={mapSelectedId}
                zoom={mapZoom}
                onCleanSelection={(e) => {
                  if (e) {
                    const shouldPreserveAirportFlow = preserveAirportFlowOnNextCleanSelectionRef.current;
                    preserveAirportFlowOnNextCleanSelectionRef.current = false;
                    setSideBarState(SideBarState.List);
                    setCleanSelection(false);
                    setMissionFilter(undefined);
                    setBranchMarker(undefined);
                    setPreSelectedBranch({ id: undefined, lat: undefined, lng: undefined });
                    if (!shouldPreserveAirportFlow) {
                      setResetAirportFlowKey(StringHelper.generateRandomString(8));
                    }

                    dispatch(missionsActions.setSelectedBranch({ id: undefined, lat: undefined, lng: undefined }));
                    dispatch(drawerActions.resetState());
                    clearMapSelectionRouteParams();
                  }
                }}
                onPanCenter={handleMapPanToOrigin}
                onBoundsChange={handleBoundsChange}
                onViewportChange={handleViewportChange}
                onUserInteractionStart={() => {
                  cancelPendingViewportSearch();
                  suppressUserInteractionUntilRef.current = 0;
                  ignoreNextBoundsFromClusterRef.current = false;
                  ignoreNextBoundsFromRestoreRef.current = false;
                  applyNextProgrammaticBoundsRef.current = false;
                  userHasPannedSinceProgrammaticMoveRef.current = true;
                  setAutoZoomOnFirstLoad(false);
                  setSuppressSearchAreaDuringInitialZoom(false);
                  setMapMode(MapMode.USER_VIEWPORT_MODE);
                }}
                onUserInteractionComplete={() => {
                  if (Date.now() < suppressUserInteractionUntilRef.current) {
                    return;
                  }
                  if (autoZoomOnFirstLoad) {
                    return;
                  }
                  setMapMode(MapMode.USER_VIEWPORT_MODE);
                  userHasPannedSinceProgrammaticMoveRef.current = true;
                }}
                onClusterClick={() => {
                  ignoreNextBoundsFromClusterRef.current = true;
                }}
                viewportCommand={viewportCommand}
                onAirportBannerPress={handleAirportBannerPress}
                resetAirportFlowKey={resetAirportFlowKey}
                onFilterMissionList={(ids: string[]) => {
                  setMissionCardProps(initialMissionCardProps.filter((x) => ids?.find((id) => id == x.id)));
                  setPinnedMissionCardProps(initialPinnedMissionCardProps);
                  setFlashMissionCardProps(initialFlashMissionCardProps.filter((x) => ids?.find((id) => id == x.id)));
                }}
              />
            )}
            {showMobileListView && hasLoadedMissions && (
              <View
                style={[
                  styles.container,
                  { backgroundColor: theme.background.primary, position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2250 },
                ]}
              >
                <MissionList
                  airportMissionCards={interactiveMissionCards.airportMissionCards}
                  missionCards={interactiveMissionCards.missionCards}
                  pinnedMissionCards={interactiveMissionCards.pinnedMissionCards}
                  flashMissionCards={interactiveMissionCards.flashMissionCards}
                  loadingSpin={loadingSideContent || shouldShowMapMissionLoading}
                  filter={missionListFilter}
                  onFilterChange={handleRegularMissionFilterChange}
                  syncWithMapFilters={true}
                  compactFiltersExpanded={compactFiltersExpanded}
                  onCompactFiltersExpandedChange={setCompactFiltersExpanded}
                  mapFilterItems={mapFilterItemsForList}
                  mapOnlyAcceptableMissions={onlyAcceptableMissions}
                  mapShowUpcomingMissions={showUpcomingMissions}
                  onMapFilterSelectionChange={(selectedIds) => handleFilters(selectedIds)}
                  onMapOnlyAcceptableMissionsChange={setOnlyAcceptableMissions}
                  onMapShowUpcomingMissionsChange={setShowUpcomingMissions}
                  onAirportBannerPress={handleAirportBannerPress}
                  showAgeRestrictedPromotion={hasNearbyDiscoverableAgeRestrictedMissions}
                  onAirportStartDateChange={setAirportAcceptStartDate}
                  airportBookingLocation={currentLocation ?? userLocation ?? serviceLocation ?? fallBackLocation}
                  autoOpenAirportBookingKey={airportStartupBookingRequest?.key}
                  autoOpenAirportBookingBranchId={airportStartupBookingRequest?.branchId}
                  autoOpenAirportBookingReturnToListView={airportStartupBookingRequest?.returnToListView}
                  resetAirportFlowKey={resetAirportFlowKey}
                  onAirportBookingVisibilityChange={setIsAirportBookingVisibleOnMobile}
                  onAirportBookingExitToOrigin={(returnToListView) => {
                    if (!returnToListView) {
                      handleMapShowList(false);
                    }
                  }}
                  onMissionListRefresh={handleMissionListRefresh}
                  onAutoOpenAirportBookingHandled={(key) => {
                    if (airportStartupBookingRequest?.key === key) {
                      setAirportStartupBookingRequest(undefined);
                    }
                  }}
                />
              </View>
            )}
            {showFilters &&
              (!isWideLayout ? (
                <>
                  <View style={styles.searchFieldContainerMobileFix} pointerEvents="box-none">
                    <CompactMissionFilters
                      expanded={compactFiltersExpanded}
                      onExpandedChange={setCompactFiltersExpanded}
                      items={filterItems}
                      selectedFilterType={missionListFilter ?? FilterType.All}
                      showMissionTypeFilters
                      onlyAcceptableMissions={onlyAcceptableMissions}
                      showUpcomingMissions={showUpcomingMissions}
                      open={openFilter}
                      onOpenChange={setOpenFilter}
                      onItemsChange={(selectedItems) => {
                        cancelOngoingMapRequests();
                        handleFilters(selectedItems);
                      }}
                      onFilterTypeChange={(next) => handleRegularMissionFilterChange(next as FilterType)}
                      onOnlyAcceptableMissionsChange={(next) => {
                        cancelOngoingMapRequests();
                        setOnlyAcceptableMissions(!next);
                      }}
                      onShowUpcomingMissionsChange={(next) => {
                        cancelOngoingMapRequests();
                        setShowUpcomingMissions(next);
                      }}
                    />
                    {!showMobileListView && isIdleMapView && !isAirportBookingVisibleOnMobile
                      && !hasBlockingMapOverlay && visiblePinnedMissionCount > 0 && (
                      <PinnedMissionsButton count={visiblePinnedMissionCount}
                        onPress={handleFeaturedMissionsPress} />
                    )}
                    {mapMode === MapMode.INITIAL_LOAD &&
                    !suppressSearchAreaDuringInitialZoom &&
                    !openFilter &&
                    (topLeftLocation !== appliedTopLeftLocation || bottomRightLocation !== appliedBottomRightLocation) && (
                      <View pointerEvents="box-none" style={{ alignItems: 'center', marginTop: 12 }}>
                        <Pressable
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            paddingHorizontal: 12,
                            paddingVertical: 8,
                            backgroundColor: theme.background.lightSea,
                            gap: 8,
                            borderRadius: 50,
                          }}
                          onPress={handleSearchThisAreaPress}
                        >
                          <FontAwesomeIcon icon={faMagnifyingGlass} size={20} color={theme.text.darkTurquoiseTwo.toString()} />
                          <Label style={{ fontSize: 13, color: theme.text.darkTurquoiseTwo, fontWeight: '600' }}>{t('Search-this-area')}</Label>
                        </Pressable>
                      </View>
                    )}
                  </View>
                </>
              ) : (
                <MapFilter
                  items={filterItems}
                  selectedFilterType={missionListFilter ?? FilterType.All}
                  showMissionTypeFilters={true}
                  onlyAcceptableMissions={onlyAcceptableMissions}
                  showUpcomingMissions={showUpcomingMissions}
                  keepSwitchesOnOneRow={true}
                  unavailableToggleLabel={t('show-unavailable-short')}
                  upcomingToggleLabel={t('show-upcoming-short')}
                  onChangeText={() => {}}
                  onClear={() => {}}
                  onFocus={() => {}}
                  onBlur={() => {}}
                  onOpenChange={setOpenFilter}
                  onKeyPress={() => {}}
                  onBack={() => {}}
                  onSelectionChangeStart={cancelOngoingMapRequests}
                  onSelectionChange={handleFilters}
                  onFilterTypeChange={(next) => handleRegularMissionFilterChange(next as FilterType)}
                  onSearchAreaPress={handleSearchThisAreaPress}
                  showSearchArea={
                    mapMode === MapMode.INITIAL_LOAD &&
                    !suppressSearchAreaDuringInitialZoom &&
                    (topLeftLocation !== appliedTopLeftLocation || bottomRightLocation !== appliedBottomRightLocation)
                  }
                  open={openFilter}
                  loading={loadingSideContent || shouldShowMapMissionLoading}
                  onOnlyAcceptableMissions={() => {
                    cancelOngoingMapRequests();
                    setOnlyAcceptableMissions((prev) => !prev);
                  }}
                  onShowUpcomingMissions={() => {
                    cancelOngoingMapRequests();
                    setShowUpcomingMissions((prev) => !prev);
                  }}
                />
              ))}
            {!isWideLayout && !isAirportBookingVisibleOnMobile && (
              <View pointerEvents="box-none" style={{ position: 'absolute', bottom: 16, left: 0, right: 0, alignItems: 'center', zIndex: 2300, elevation: 12 }}>
                <PillButton
                  style={styles.mobileViewToggle}
                  textStyle={styles['mobileViewToggle.text']}
                  icon={
                    <FontAwesomeIcon icon={showMobileListView ? faMapLocationDot : faList} size={showMobileListView ? 24 : 20} color={mobileToggleIconColor} />
                  }
                  title={showMobileListView ? t('view_map_button') : t('view_list_button')}
                  onPress={(event) => {
                    event.stopPropagation?.();
                    handleMapShowList(!showMobileListView);
                  }}
                />
              </View>
            )}
          </View>
          {isWideLayout && hasLoadedMissions && (
            <View style={responsiveStyles('sideBar')}>
              <View style={{ flex: 1, display: sideBarState === SideBarState.List ? 'flex' : 'none' }}>
                <View style={[responsiveStyles('sideBarContent'), { backgroundColor: theme.background.primary }]}>
                  <MissionList
                    airportMissionCards={interactiveMissionCards.airportMissionCards}
                    missionCards={interactiveMissionCards.missionCards}
                    pinnedMissionCards={interactiveMissionCards.pinnedMissionCards}
                    flashMissionCards={interactiveMissionCards.flashMissionCards}
                    loadingSpin={loadingSideContent || shouldShowMapMissionLoading}
                    filter={missionListFilter}
                    onFilterChange={handleRegularMissionFilterChange}
                    onAirportBannerPress={handleAirportBannerPress}
                    showAgeRestrictedPromotion={hasNearbyDiscoverableAgeRestrictedMissions}
                    onAirportStartDateChange={setAirportAcceptStartDate}
                    airportBookingLocation={currentLocation ?? userLocation ?? serviceLocation ?? fallBackLocation}
                    autoOpenAirportBookingKey={airportStartupBookingRequest?.key}
                    autoOpenAirportBookingBranchId={airportStartupBookingRequest?.branchId}
                    autoOpenAirportBookingReturnToListView={airportStartupBookingRequest?.returnToListView}
                    resetAirportFlowKey={resetAirportFlowKey}
                    onAirportBookingExitToOrigin={() => {}}
                    onAutoOpenAirportBookingHandled={(key) => {
                      if (airportStartupBookingRequest?.key === key) {
                        setAirportStartupBookingRequest(undefined);
                      }
                    }}
                  />
                </View>
              </View>
              {sideBarState !== SideBarState.List && (
                <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1 }}>
                  {desktopSideBarContent}
                </ScrollView>
              )}
            </View>
          )}
          {isMapPopupActive && <ProfileManager ref={profileManager} />}
        </View>
      )}
    </AuthGuard>
  );
};

const responsiveStyle = CreateResponsiveStyle(
  {
    container: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      height: '100%',
    },
    mapContainer: {
      flexGrow: 1,
      height: '100%',
    },
    sideBar: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 350,
      width: '30%',
      height: '100%',
      flexGrow: 1,
      backgroundColor: 'white',
    },
    sideBarTitle: {
      borderBottomWidth: 1,
      borderBottomColor: '#e9e9e980',
      padding: 16,
    },
    sideBarContent: {
      flexGrow: 1,
      flexShrink: 1,
      height: '100%',
    },
  },
  {
    container: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      height: '100%',
    },
    mapContainer: {
      width: '100%',
      height: '100%',
    },
    sideBar: {
      width: 0,
      height: '100%',
      flexGrow: 1,
    },
  },
);

export default CustomMap;
